/**
 * Scout Studio — kreator "O Twojej firmie" (6 kroków, baza wiedzy Scouta).
 * Pasek uzupełnienia profilu + zapis do MongoDB po każdym kroku.
 */
import { useEffect, useState } from 'react';
import { API } from '@/utils/api';
import {
  Building2, Package, Goal, Eye, Star, Palette, ChevronLeft, ChevronRight,
  Check, Loader2, Plus, X, Archive, Sparkles,
} from 'lucide-react';

const panel = 'rounded-2xl border border-[var(--sc-border)] bg-[var(--sc-surface)]';
const inputCls = 'w-full rounded-xl bg-[var(--sc-input)] border border-[var(--sc-border2)] px-3.5 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-orange-400/50';

const STEPS = [
  { id: 1, name: 'Podstawy', icon: Building2 },
  { id: 2, name: 'Oferta i klient', icon: Package },
  { id: 3, name: 'Cel', icon: Goal },
  { id: 4, name: 'Widoczność', icon: Eye },
  { id: 5, name: 'Opinie i konkurencja', icon: Star },
  { id: 6, name: 'Styl i materiały', icon: Palette },
];

const GOALS_30D = ['Więcej zapytań od klientów', 'Sprzedaż produktu', 'Widoczność lokalna',
  'Promocja konkretnej usługi', 'Start od zera', 'Budowa zaufania i wizerunku'];

const VIS_FIELDS = [
  { key: 'www', label: 'Strona WWW', ph: 'np. https://twojafirma.pl' },
  { key: 'google', label: 'Google Moja Firma', ph: 'link do wizytówki lub "mam"' },
  { key: 'facebook', label: 'Facebook', ph: 'link do profilu' },
  { key: 'instagram', label: 'Instagram', ph: 'link lub @nazwa' },
  { key: 'tiktok', label: 'TikTok', ph: 'link lub @nazwa' },
  { key: 'marketplace', label: 'Marketplace (OLX, Allegro…)', ph: 'gdzie sprzedajesz' },
  { key: 'catalogs', label: 'Katalogi firm', ph: 'np. katalog APIS / Caniva' },
  { key: 'portals', label: 'Portale branżowe', ph: 'np. portal branżowy' },
];

function localCompleteness(f) {
  const vis = f.visibility || {}; const rev = f.reviews || {};
  const checks = [
    !!(f.name || '').trim(), !!(f.industry || '').trim(), !!(f.city || '').trim(),
    !!(f.area || '').trim(), !!(f.description || '').trim(), !!(f.offer || '').trim(),
    !!(f.prices || '').trim(), (f.usp || []).length > 0, !!(f.audience || '').trim(),
    !!(f.goal_30d || '').trim(), !!(f.goal_6m || '').trim(),
    Object.values(vis).some((v) => (v || '').trim()),
    Object.values(rev).some((v) => (v || '').trim()),
    (f.competitors || []).length > 0, !!(f.tone || '').trim(),
    !!((f.avoid || '').trim() || (f.banned_words || '').trim()), !!(f.materials || '').trim(),
  ];
  return Math.round((checks.filter(Boolean).length / checks.length) * 100);
}

export default function ScoutFirma({ client, onSaved, onArchived, onDone }) {
  const [step, setStep] = useState(1);
  const [form, setForm] = useState(() => ({ ...client }));
  const [saving, setSaving] = useState(false);
  const [savedMsg, setSavedMsg] = useState('');
  const [confirmArchive, setConfirmArchive] = useState(false);

  useEffect(() => { setForm({ ...client }); setStep(1); setSavedMsg(''); }, [client.id]); // eslint-disable-line react-hooks/exhaustive-deps

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const setVis = (k, v) => setForm((f) => ({ ...f, visibility: { ...(f.visibility || {}), [k]: v } }));
  const setRev = (k, v) => setForm((f) => ({ ...f, reviews: { ...(f.reviews || {}), [k]: v } }));
  const pct = localCompleteness(form);

  const save = async (goNext) => {
    setSaving(true); setSavedMsg('');
    try {
      const body = {
        name: form.name, industry: form.industry, city: form.city, area: form.area,
        description: form.description, offer: form.offer, prices: form.prices,
        usp: form.usp || [], audience: form.audience, goal_30d: form.goal_30d,
        goal_6m: form.goal_6m, visibility: form.visibility || {}, reviews: form.reviews || {},
        competitors: form.competitors || [], tone: form.tone, avoid: form.avoid,
        banned_words: form.banned_words, materials: form.materials,
      };
      const res = await fetch(`${API}/scout/clients/${client.id}`, {
        method: 'PUT', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(body),
      });
      const data = await res.json();
      if (data?.ok && data.client) {
        onSaved?.(data.client);
        if (goNext && step < 6) setStep(step + 1);
        else if (goNext && step === 6) { setSavedMsg('Profil zapisany. Scout korzysta z niego we wszystkich modułach.'); onDone?.(); }
        else setSavedMsg('Zapisano.');
      } else setSavedMsg(data?.message || 'Nie udało się zapisać.');
    } catch { setSavedMsg('Błąd połączenia. Spróbuj ponownie.'); }
    finally { setSaving(false); }
  };

  const archive = async () => {
    try {
      const res = await fetch(`${API}/scout/clients/${client.id}`, { method: 'DELETE' });
      const data = await res.json();
      if (data?.ok) onArchived?.();
    } catch { /* noop */ }
  };

  return (
    <div className="space-y-6" data-testid="view-firma">
      <div className="mb-2">
        <h2 className="text-lg font-bold flex items-center gap-2"><Building2 className="w-5 h-5 text-[#FF8A3D]" /> O Twojej firmie</h2>
        <p className="text-xs text-[var(--sc-t45)] mt-0.5 ml-7">Baza wiedzy Scouta. Im lepiej uzupełniony profil, tym trafniejsze treści, analizy i podpowiedzi.</p>
      </div>

      {/* Pasek uzupełnienia */}
      <div className={`${panel} p-4`} data-testid="profile-progress">
        <div className="flex items-center justify-between mb-2">
          <span className="text-xs font-semibold text-[var(--sc-t60)]">Profil uzupełniony w <span className="text-[#FF8A3D] font-extrabold">{pct}%</span></span>
          {pct < 40 && <span className="text-[11px] text-[var(--sc-t40)]">Brakuje mi kilku informacji, żeby dawać trafne odpowiedzi</span>}
          {pct >= 70 && <span className="text-[11px] text-emerald-400 flex items-center gap-1"><Sparkles className="w-3 h-3" /> Bardzo dobry profil</span>}
        </div>
        <div className="h-2.5 rounded-full bg-[var(--sc-surface3)] overflow-hidden">
          <div className="h-full rounded-full bg-[#F26A21] transition-all duration-700" style={{ width: `${pct}%` }} />
        </div>
      </div>

      {/* Kroki */}
      <div className="flex flex-wrap gap-2" data-testid="wizard-steps">
        {STEPS.map((s) => {
          const Icon = s.icon; const on = step === s.id; const done = step > s.id;
          return (
            <button key={s.id} onClick={() => setStep(s.id)} data-testid={`wizard-step-${s.id}`}
              className={`inline-flex items-center gap-2 rounded-xl px-3 py-2 text-xs font-semibold transition border ${on ? 'bg-[#F26A21] border-transparent text-white' : done ? 'border-emerald-400/30 bg-emerald-500/10 text-emerald-300' : 'border-[var(--sc-border2)] text-[var(--sc-t50)] hover:text-[var(--sc-t100)] hover:bg-[var(--sc-hover)]'}`}>
              {done ? <Check className="w-3.5 h-3.5" /> : <Icon className="w-3.5 h-3.5" />} {s.id}. {s.name}
            </button>
          );
        })}
      </div>

      {/* Formularz kroku */}
      <div className={`${panel} p-6 space-y-5`} data-testid={`wizard-form-step-${step}`}>
        {step === 1 && (
          <>
            <StepTitle t="Podstawy" d="Kim jesteś i gdzie działasz." />
            <Field label="Nazwa firmy"><input className={inputCls} value={form.name || ''} onChange={(e) => set('name', e.target.value)} data-testid="input-name" /></Field>
            <div className="grid sm:grid-cols-2 gap-4">
              <Field label="Branża"><input className={inputCls} value={form.industry || ''} onChange={(e) => set('industry', e.target.value)} placeholder="np. salon beauty, hodowla psów, perfumy" data-testid="input-industry" /></Field>
              <Field label="Miasto / lokalizacja"><input className={inputCls} value={form.city || ''} onChange={(e) => set('city', e.target.value)} placeholder="np. Warszawa" data-testid="input-city" /></Field>
            </div>
            <Field label="Obszar działania"><input className={inputCls} value={form.area || ''} onChange={(e) => set('area', e.target.value)} placeholder="np. Warszawa i okolice / cała Polska / online" data-testid="input-area" /></Field>
            <Field label="Krótki opis firmy"><textarea className={`${inputCls} min-h-[90px]`} value={form.description || ''} onChange={(e) => set('description', e.target.value)} placeholder="Czym się zajmujesz, od kiedy, co jest dla Ciebie ważne…" data-testid="input-description" /></Field>
          </>
        )}

        {step === 2 && (
          <>
            <StepTitle t="Oferta i klient" d="Co sprzedajesz i komu." />
            <Field label="Produkty / usługi"><textarea className={`${inputCls} min-h-[90px]`} value={form.offer || ''} onChange={(e) => set('offer', e.target.value)} placeholder="Wypisz najważniejsze produkty lub usługi…" data-testid="input-offer" /></Field>
            <Field label="Ceny / widełki"><input className={inputCls} value={form.prices || ''} onChange={(e) => set('prices', e.target.value)} placeholder="np. makijaż permanentny od 500 zł, pakiety 800-1200 zł" data-testid="input-prices" /></Field>
            <ChipList label="Wyróżniki (USP)" hint="Co robisz lepiej niż inni? Dodaj 2-4 punkty." items={form.usp || []} onChange={(v) => set('usp', v)} ph="np. 12 lat doświadczenia" testid="usp" />
            <Field label="Klient idealny"><input className={inputCls} value={form.audience || ''} onChange={(e) => set('audience', e.target.value)} placeholder="np. kobiety 25-45 z Warszawy, właściciele psów…" data-testid="input-audience" /></Field>
          </>
        )}

        {step === 3 && (
          <>
            <StepTitle t="Cel" d="Co chcesz osiągnąć? Scout dopasuje do tego działania." />
            <Field label="Cel na najbliższe 30 dni">
              <div className="flex flex-wrap gap-2" data-testid="goal-30d-options">
                {GOALS_30D.map((g) => (
                  <button key={g} onClick={() => set('goal_30d', g)} data-testid={`goal-option-${g.slice(0, 10)}`}
                    className={`rounded-xl px-3.5 py-2 text-xs font-semibold border transition ${form.goal_30d === g ? 'bg-[#F26A21] border-transparent text-white' : 'border-[var(--sc-border2)] text-[var(--sc-t60)] hover:bg-[var(--sc-hover)]'}`}>
                    {g}
                  </button>
                ))}
              </div>
            </Field>
            <Field label="Gdzie chcesz być za 6 miesięcy?"><textarea className={`${inputCls} min-h-[80px]`} value={form.goal_6m || ''} onChange={(e) => set('goal_6m', e.target.value)} placeholder="np. 20 zapytań miesięcznie, stała obecność na Instagramie, pierwsze 3 umowy B2B…" data-testid="input-goal-6m" /></Field>
          </>
        )}

        {step === 4 && (
          <>
            <StepTitle t="Widoczność" d="Gdzie już jesteś? Wpisz linki albo krótko opisz. Puste pole = brak." />
            <div className="grid sm:grid-cols-2 gap-4">
              {VIS_FIELDS.map((f) => (
                <Field key={f.key} label={f.label}>
                  <input className={inputCls} value={(form.visibility || {})[f.key] || ''} onChange={(e) => setVis(f.key, e.target.value)} placeholder={f.ph} data-testid={`input-vis-${f.key}`} />
                </Field>
              ))}
            </div>
          </>
        )}

        {step === 5 && (
          <>
            <StepTitle t="Opinie i konkurencja" d="Zaufanie i rynek — Scout będzie z tego korzystał w analizach." />
            <div className="grid sm:grid-cols-2 gap-4">
              <Field label="Gdzie są Twoje opinie? (linki)"><input className={inputCls} value={(form.reviews || {}).links || ''} onChange={(e) => setRev('links', e.target.value)} placeholder="np. link do opinii Google / Facebook" data-testid="input-reviews-links" /></Field>
              <Field label="Ile masz opinii i jaka średnia?"><input className={inputCls} value={(form.reviews || {}).count || ''} onChange={(e) => setRev('count', e.target.value)} placeholder="np. 48 opinii, średnia 4.8" data-testid="input-reviews-count" /></Field>
            </div>
            <Field label="Co klienci najczęściej chwalą?"><textarea className={`${inputCls} min-h-[70px]`} value={(form.reviews || {}).praises || ''} onChange={(e) => setRev('praises', e.target.value)} placeholder="np. terminowość, efekt naturalny, kontakt…" data-testid="input-reviews-praises" /></Field>
            <ChipList label="Konkurenci do monitorowania" hint="Nazwy lub linki — Scout użyje ich w porównaniach." items={form.competitors || []} onChange={(v) => set('competitors', v)} ph="np. https://konkurent.pl" testid="competitors" />
          </>
        )}

        {step === 6 && (
          <>
            <StepTitle t="Styl i materiały" d="Jak Scout ma mówić w Twoim imieniu i z czego korzystać." />
            <Field label="Ton komunikacji"><input className={inputCls} value={form.tone || ''} onChange={(e) => set('tone', e.target.value)} placeholder="np. ciepły i konkretny / premium i elegancki" data-testid="input-tone" /></Field>
            <Field label="Czego Scout ma unikać?"><textarea className={`${inputCls} min-h-[80px]`} value={form.avoid || ''} onChange={(e) => set('avoid', e.target.value)} placeholder="np. nachalnej sprzedaży, obiecywania efektów, tematów cenowych…" data-testid="input-avoid" /></Field>
            <Field label="Zakazane słowa"><input className={inputCls} value={form.banned_words || ''} onChange={(e) => set('banned_words', e.target.value)} placeholder="np. tanio, najtaniej, promocja -50%" data-testid="input-banned-words" /></Field>
            <Field label="Materiały, które masz"><textarea className={`${inputCls} min-h-[70px]`} value={form.materials || ''} onChange={(e) => set('materials', e.target.value)} placeholder="np. 40 zdjęć, logo, cennik PDF, filmy z realizacji…" data-testid="input-materials" /></Field>
          </>
        )}

        {/* Nawigacja kroków */}
        <div className="flex items-center gap-2 pt-2 border-t border-[var(--sc-border)]">
          {step > 1 && (
            <button onClick={() => setStep(step - 1)} data-testid="wizard-prev"
              className="inline-flex items-center gap-1.5 rounded-xl border border-[var(--sc-border2)] px-4 py-2.5 text-sm font-semibold text-[var(--sc-t60)] hover:bg-[var(--sc-hover)] transition">
              <ChevronLeft className="w-4 h-4" /> Wstecz
            </button>
          )}
          <button onClick={() => save(true)} disabled={saving} data-testid="wizard-next"
            className="inline-flex items-center gap-1.5 rounded-xl bg-[#F26A21] hover:bg-[#FF7C33] px-5 py-2.5 text-sm font-bold text-white transition disabled:opacity-50">
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : step === 6 ? <Check className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
            {step === 6 ? 'Zapisz profil' : 'Zapisz i dalej'}
          </button>
          {savedMsg && <span className="text-xs text-emerald-400" data-testid="wizard-saved-msg">{savedMsg}</span>}
        </div>
      </div>

      {/* Archiwizacja */}
      <div className={`${panel} p-4 flex flex-wrap items-center gap-3`}>
        <Archive className="w-4 h-4 text-[var(--sc-t40)]" />
        <div className="flex-1 text-xs text-[var(--sc-t45)]">Nie pracujesz już z tym klientem? Możesz zarchiwizować profil — zniknie z listy, ale dane zostaną.</div>
        {!confirmArchive ? (
          <button onClick={() => setConfirmArchive(true)} data-testid="archive-client-btn"
            className="rounded-lg border border-[var(--sc-border2)] px-3.5 py-2 text-xs font-semibold text-[var(--sc-t50)] hover:text-red-400 hover:border-red-400/40 transition">
            Archiwizuj klienta
          </button>
        ) : (
          <div className="flex items-center gap-2">
            <span className="text-xs text-[var(--sc-t60)]">Na pewno?</span>
            <button onClick={archive} data-testid="archive-confirm-btn" className="rounded-lg bg-red-500/15 border border-red-400/40 px-3 py-1.5 text-xs font-bold text-red-400">Tak, archiwizuj</button>
            <button onClick={() => setConfirmArchive(false)} data-testid="archive-cancel-btn" className="rounded-lg border border-[var(--sc-border2)] px-3 py-1.5 text-xs font-semibold text-[var(--sc-t50)]">Anuluj</button>
          </div>
        )}
      </div>
    </div>
  );
}

function StepTitle({ t, d }) {
  return (
    <div>
      <div className="font-bold">{t}</div>
      <div className="text-xs text-[var(--sc-t45)] mt-0.5">{d}</div>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <div>
      <div className="text-xs font-semibold text-[var(--sc-t55)] mb-1.5">{label}</div>
      {children}
    </div>
  );
}

function ChipList({ label, hint, items, onChange, ph, testid }) {
  const [val, setVal] = useState('');
  const add = () => {
    const v = val.trim();
    if (!v || items.includes(v)) return;
    onChange([...items, v]); setVal('');
  };
  return (
    <div>
      <div className="text-xs font-semibold text-[var(--sc-t55)] mb-1.5">{label}</div>
      {hint && <div className="text-[11px] text-[var(--sc-t35)] mb-2">{hint}</div>}
      <div className="flex gap-2">
        <input className={inputCls} value={val} onChange={(e) => setVal(e.target.value)}
          onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); add(); } }} placeholder={ph} data-testid={`input-${testid}`} />
        <button onClick={add} data-testid={`add-${testid}`}
          className="shrink-0 rounded-xl border border-orange-400/40 bg-orange-500/10 px-3.5 text-orange-300 hover:bg-orange-500/20 transition"><Plus className="w-4 h-4" /></button>
      </div>
      {items.length > 0 && (
        <div className="flex flex-wrap gap-2 mt-2.5" data-testid={`list-${testid}`}>
          {items.map((it) => (
            <span key={it} className="inline-flex items-center gap-1.5 rounded-full bg-[var(--sc-surface3)] border border-[var(--sc-border)] px-3 py-1 text-xs font-semibold text-[var(--sc-t80)]">
              {it}
              <button onClick={() => onChange(items.filter((x) => x !== it))} className="text-[var(--sc-t40)] hover:text-red-400"><X className="w-3 h-3" /></button>
            </span>
          ))}
        </div>
      )}
    </div>
  );
}
